import React, { useCallback, FC } from 'react';

import {
  TreeViewSelectNodeHeader,
  TreeViewSelectGroup,
  TreeViewSelectInput,
  TreeViewSelectItem,
  TreeViewSelectNode,
  TreeViewSelectList,
  TreeViewSelect,
} from './tree-view-select';
import {
  DialogContent,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
  Dialog,
} from '../dialog/dialog';
import { Typography } from '../typography/typography';
import { Button } from '../button/button';

interface Node {
  label: string;
  value: string;
}

interface Item {
  node: Node['value'];
  label: string;
  value: string;
}

export interface TreeViewSelectDialogProps {
  componentProps?: Partial<React.ComponentProps<typeof TreeViewSelect>>;
  confirmButtonLabel: string;
  cancelButtonLabel: string;
  children: React.ReactNode;
  nodeEditedLabel: string;
  nodeGroupLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  value: string[];
  options: Item[];
  nodes: Node[];
  title: string;
  onValueChange: (value: string[]) => void;
}

export const TreeViewSelectDialog: FC<TreeViewSelectDialogProps> = ({
  confirmButtonLabel,
  cancelButtonLabel,
  nodeEditedLabel,
  componentProps,
  nodeGroupLabel,
  onValueChange,
  placeholder,
  children,
  disabled,
  options,
  title,
  value,
  nodes,
}) => {
  const [selected, setSelected] = React.useState<string[]>(value);

  React.useEffect(() => {
    setSelected(value);
  }, [value]);

  const nodesWithOptions = React.useMemo(
    () =>
      nodes.map((node) => {
        const items = options.filter((item) => item.node === node.value);
        return {
          node,
          items,
          values: items.map((item) => item.value),
          keywords: items.map((item) => item.label),
        };
      }),
    [nodes, options],
  );

  const valuePerNode = React.useMemo(
    () =>
      nodesWithOptions.reduce(
        (acc, { node, values }) => ({
          ...acc,
          [node.value]: values.filter((value) => selected.includes(value)),
        }),
        {} as Record<string, string[]>,
      ),
    [nodesWithOptions, selected],
  );

  const isDisabled = useCallback(
    (nodeValue: string, itemValue: string) => {
      const nodeSelectedValues = valuePerNode[nodeValue];
      if (!nodeSelectedValues) return false;
      return nodeSelectedValues.length === 1 && nodeSelectedValues.includes(itemValue);
    },
    [valuePerNode],
  );

  return (
    <Dialog onOpenChange={() => setSelected(value)}>
      <DialogTrigger disabled={disabled} asChild>
        {children}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader className="p-4">
          <DialogTitle>
            <Typography variant="h300">{title}</Typography>
          </DialogTitle>
        </DialogHeader>
        <div className="p-4">
          <TreeViewSelect {...componentProps} onSelectedChange={setSelected} selected={value}>
            <TreeViewSelectList>
              <TreeViewSelectInput placeholder={placeholder} />
              {nodesWithOptions.map(({ node, items, keywords, values }) => (
                <TreeViewSelectNode
                  label={
                    <div className="space-x-1">
                      <Typography variant="labelMediumBook" element="span">
                        {node.label}
                      </Typography>
                      <Typography
                        className="text-weak group-[&]/tree-view-select-node-selected:hidden"
                        variant="labelXSmallBook"
                        element="span"
                      >
                        ({nodeEditedLabel})
                      </Typography>
                    </div>
                  }
                  keywords={[node.label, ...keywords]}
                  path={node.value}
                  key={node.label}
                  values={values}
                >
                  <TreeViewSelectNodeHeader variant="labelMediumBook">
                    {node.label}
                  </TreeViewSelectNodeHeader>
                  <TreeViewSelectGroup heading={nodeGroupLabel}>
                    {items.map(({ label, value }) => (
                      <TreeViewSelectItem
                        disabled={isDisabled(node.value, value)}
                        keywords={[label]}
                        value={value}
                        key={value}
                      >
                        {label}
                      </TreeViewSelectItem>
                    ))}
                  </TreeViewSelectGroup>
                </TreeViewSelectNode>
              ))}
            </TreeViewSelectList>
          </TreeViewSelect>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button onClick={() => setSelected(value)} variant="tertiary" size="large" wide>
              {cancelButtonLabel}
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button onClick={() => onValueChange(selected)} variant="primary" size="large" wide>
              {confirmButtonLabel}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
